import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { Alert, Stack, Typography } from "@mui/material";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe, type StripeConstructorOptions } from "@stripe/stripe-js";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import Section from "@/components/Section";
import { useBookingContext } from "@/contexts/BookingContext";
import { useFeatureFlags } from "@/contexts/FeaturesContext";
import { useCampground } from "@/hooks";
import { useCountdown } from "@/hooks/useCountdown";
import { useStepNavigation } from "@/hooks/useStepNavigation";
import { useStripePayment } from "@/hooks/useStripePayment";
import { logError } from "@/utils/logError";
import { computeAmountToPayOnline, formatCurrency } from "@/utils/pricing";
import BookingStepLayout from "./components/BookingStepLayout";
import SessionTimeoutAlert from "./components/SessionTimeoutAlert";

type StripeLocale = NonNullable<StripeConstructorOptions["locale"]>;

function PaymentForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const { pollPaymentStatus } = useStripePayment();
  const { state } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { i18n } = useLingui();
  const { isExpired } = useCountdown(state.bookingCreatedAt, campgroundData.session_timeout);
  const price = state.price;
  const amountToPayOnline = price ? computeAmountToPayOnline(price) : 0;
  const formatPrice = (value: number) =>
    price ? formatCurrency(value, price.currency, i18n.locale) : null;

  const submit = useCallback(async () => {
    let result = false;
    if (!stripe || !elements || isProcessing) return result;

    setIsProcessing(true);

    try {
      const billing = state.billing;
      const fullName = [billing.name, billing.lastname].filter(Boolean).join(" ").trim();

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`,
          payment_method_data: {
            billing_details: {
              name: fullName,
              email: billing.mail || undefined,
              phone: billing.phone || undefined,
              address: {
                country: billing.country?.code?.toUpperCase() || "DE",
              },
            },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        // Intentional: Stripe card errors (e.g. "Card declined") are user-facing by design
        enqueueSnackbar(error.message || t`Payment failed. Please try again.`, {
          variant: "error",
        });
        return false;
      }

      if (paymentIntent?.status === "succeeded") {
        const status = await pollPaymentStatus(state.bookingId!, paymentIntent.id);

        if (status === "pending") {
          logError(new Error("Payment status polling timed out"), {
            bookingId: state.bookingId,
            paymentIntentId: paymentIntent.id,
          });
          enqueueSnackbar(t`Payment status could not be confirmed. Please contact support.`, {
            variant: "error",
          });
          // optimistically proceed — webhook may still confirm later
          onSuccess();
          result = true;
        } else if (status === "confirmed") {
          onSuccess();
          result = true;
        } else {
          enqueueSnackbar(t`Payment failed. Please try again.`, {
            variant: "error",
          });
          result = false;
        }
      }
    } catch (err) {
      logError(err);
      enqueueSnackbar(t`An unexpected error occurred`, { variant: "error" });
      result = false;
    } finally {
      setIsProcessing(false);
    }
    return result;
  }, [
    stripe,
    elements,
    isProcessing,
    state.billing,
    state.bookingId,
    pollPaymentStatus,
    onSuccess,
  ]);

  return (
    <BookingStepLayout
      nextLoading={isProcessing}
      nextAction={submit}
      nextButtonText={t`Pay now`}
      nextDisabled={isExpired}
    >
      <Stack spacing={2}>
        <SessionTimeoutAlert />
        <Section title={t`Payment`} sx={{ minHeight: "300px" }}>
          <Typography sx={{ mb: 2 }}>
            <Trans>
              Amount to pay: <b>{formatPrice(amountToPayOnline)}</b>
            </Trans>
          </Typography>
          <PaymentElement />
        </Section>
      </Stack>
    </BookingStepLayout>
  );
}

function DemoPayment() {
  const { goToNextStep } = useStepNavigation();
  const handleNext = useCallback(async () => {
    goToNextStep();
    return true;
  }, [goToNextStep]);
  return (
    <BookingStepLayout nextAction={handleNext} nextButtonText={t`Confirm demo payment`}>
      <Stack spacing={2}>
        <SessionTimeoutAlert />
        <Section title={t`Payment`}>
          <Alert severity="info">
            <Trans>Demo mode: no payment will be processed.</Trans>
          </Alert>
        </Section>
      </Stack>
    </BookingStepLayout>
  );
}

function DefaultStripePayment() {
  const { i18n } = useLingui();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | false>(false);
  const { goToNextStep } = useStepNavigation();

  const { createPaymentIntent, pollPaymentStatus } = useStripePayment();
  const { state } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const amountToPayOnline = state.price ? computeAmountToPayOnline(state.price) : 0;

  const stripePublicKey = useMemo(() => {
    const selectedCampsite = state.selectedCampsite;
    const paymentInfo =
      selectedCampsite?.campsite_type === "accommodation"
        ? campgroundData.payment_infos?.rentals
        : campgroundData.payment_infos?.pitches;

    return paymentInfo?.payment_provider_settings?.data?.public_key || null;
  }, [state.selectedCampsite, campgroundData.payment_infos]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      try {
        // Detect return from a redirect-based payment (e.g. Klarna).
        // Stripe appends ?payment_intent=...&redirect_status=... to the return_url.
        const urlParams = new URLSearchParams(window.location.search);
        const paymentIntentIdFromUrl = urlParams.get("payment_intent");
        const redirectStatus = urlParams.get("redirect_status");

        if (paymentIntentIdFromUrl) {
          window.history.replaceState({}, "", window.location.pathname);

          if (redirectStatus !== "failed" && state.bookingId) {
            const status = await pollPaymentStatus(state.bookingId, paymentIntentIdFromUrl);
            if (status === "confirmed") {
              goToNextStep();
              return;
            }
            logError(new Error("Payment status polling timed out after redirect"), {
              bookingId: state.bookingId,
              paymentIntentId: paymentIntentIdFromUrl,
            });
            setInitError(t`Payment status could not be confirmed. Please contact support.`);
            return;
          }
          // For 'failed': fall through so the user can retry with the same payment intent.
        }

        const response = await createPaymentIntent(amountToPayOnline, state.bookingId!);

        if (response.error) {
          if (response.error.code === "online_payment_disabled") {
            setInitError(
              t`Online payment is currently unavailable for this campground. Please contact them directly or try again later.`,
            );
          } else {
            logError(new Error(response.error.message), { context: "createPaymentIntent" });
            enqueueSnackbar(t`Payment failed. Please try again.`, { variant: "error" });
            setInitError(
              t`Payment could not be initialized. Please refresh the page and try again.`,
            );
          }
          return;
        }

        if (response.clientSecret && response.stripeAccountId) {
          setClientSecret(response.clientSecret);

          setStripePromise(
            loadStripe(stripePublicKey!, {
              locale: i18n.locale as StripeLocale,
              stripeAccount: response.stripeAccountId,
              developerTools: {
                assistant: {
                  enabled: false,
                },
              },
            }),
          );
        }
      } catch (err) {
        logError(err);
        enqueueSnackbar(t`Failed to initialize payment. Please try again.`, { variant: "error" });
        setInitError(t`Payment could not be initialized. Please refresh the page and try again.`);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [
    amountToPayOnline,
    state.bookingId,
    stripePublicKey,
    createPaymentIntent,
    pollPaymentStatus,
    goToNextStep,
    i18n.locale,
  ]);

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <SessionTimeoutAlert />
        <Section title={t`Payment`} sx={{ minHeight: "300px" }}></Section>
      </Stack>
    );
  }

  if (initError || !stripePromise || !clientSecret) {
    return (
      <Stack spacing={2}>
        <SessionTimeoutAlert />
        <Section title={t`Payment`}>
          <Alert severity="error">
            {initError || (
              <Trans>
                Payment could not be initialized. Please refresh the page and try again.
              </Trans>
            )}
          </Alert>
        </Section>
      </Stack>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: "flat" },
      }}
    >
      <PaymentForm onSuccess={goToNextStep} />
    </Elements>
  );
}

export default function StripePayment() {
  const { flags } = useFeatureFlags();
  return flags.demoMode ? <DemoPayment /> : <DefaultStripePayment />;
}
